import { useCallback, useState } from 'react';
import type { IndexViewType } from '../../../../components/index-page/useIndexViewType';

/**
 * Table or list view for the plantilla, remembered per module.
 *
 * **The TABLE is the default here**, which is the one thing this differs from
 * `useIndexViewType` in. The staffing pattern is read by comparing one value down a column —
 * how many slots are authorized, how many are vacant, what grade a post carries — and that
 * is what a table is for. Everywhere else the list is the default, because a reader arriving
 * at a module is usually looking for one record.
 *
 * Its own hook rather than a flag on the shared one: a default is a product decision per
 * surface, and threading it through the shared hook would put plantilla's choice in the
 * signature every other module reads.
 *
 * The stored key is shared with the standard hook, so a reader who switched view before this
 * existed keeps the view they chose — only an explicit stored `list` overrides the default.
 */
export function usePlantillaViewType(module: string) {
  const key = `index.${module}.viewType`;

  const [viewType, setViewTypeState] = useState<IndexViewType>(() => {
    try {
      return localStorage.getItem(key) === 'list' ? 'list' : 'table';
    } catch {
      // Storage throws outright where site data is blocked, and the list must still draw.
      return 'table';
    }
  });

  const setViewType = useCallback(
    (next: IndexViewType) => {
      setViewTypeState(next);

      try {
        localStorage.setItem(key, next);
      } catch {
        // Remembering the preference is a convenience, never a requirement.
      }
    },
    [key],
  );

  return { viewType, setViewType };
}
